import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const COLS = 4, ROWS = 5, TILE_H = 80;

function newRow() { return Math.floor(Math.random() * COLS); }

export function PianoTiles({ onScore }) {
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const [rows, setRows] = useState([]); // each: {id, col, y}
  const [offset, setOffset] = useState(0);
  const raf = useRef(null);
  const speed = useRef(2.2);
  const idRef = useRef(0);
  const nextSpawnY = useRef(0);
  const reported = useRef(false);

  const start = () => {
    const init = [];
    for (let i = 0; i < ROWS + 2; i++) init.push({ id: idRef.current++, col: newRow(), y: -i * TILE_H });
    setRows(init); setScore(0); setOver(false); setRunning(true); setOffset(0);
    speed.current = 2.2; reported.current = false;
  };

  useEffect(() => {
    if (!running) return;
    const loop = () => {
      setRows((rs) => {
        let moved = rs.map((r) => ({ ...r, y: r.y + speed.current }));
        // missed tile (fell past bottom without tap)
        const bottom = ROWS * TILE_H;
        const missed = moved.find((r) => r.y > bottom && !r.tapped);
        if (missed) { setRunning(false); setOver(true); return moved; }
        moved = moved.filter((r) => r.y < bottom + TILE_H);
        // spawn new at top
        const topY = Math.min(...moved.map((r) => r.y));
        if (topY > -TILE_H) moved.push({ id: idRef.current++, col: newRow(), y: topY - TILE_H });
        return moved;
      });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [running]);

  const tap = useCallback((col) => {
    if (!running) return;
    setRows((rs) => {
      // find the lowest untapped tile in this column near the bottom
      const candidates = rs.filter((r) => r.col === col && !r.tapped && r.y > 0 && r.y < ROWS * TILE_H);
      if (!candidates.length) { setRunning(false); setOver(true); return rs; } // wrong tap
      const target = candidates.reduce((a, b) => (a.y > b.y ? a : b));
      setScore((s) => s + 1);
      speed.current = Math.min(7, speed.current + 0.06);
      return rs.map((r) => (r.id === target.id ? { ...r, tapped: true } : r));
    });
  }, [running]);

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 50); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="piano-tiles">
      <p className="font-mono text-neon-cyan text-lg mb-3" data-testid="piano-score">SCORE: {score}</p>
      <div className="relative bg-zinc-950 border-2 border-zinc-700 overflow-hidden" style={{ width: COLS * 70, maxWidth: "92vw", height: ROWS * TILE_H }}>
        {/* column guides */}
        {Array.from({ length: COLS - 1 }).map((_, i) => (
          <div key={i} className="absolute top-0 bottom-0 border-r border-zinc-800" style={{ left: `${((i + 1) / COLS) * 100}%` }} />
        ))}
        {rows.map((r) => (
          <div key={r.id} className="absolute rounded-sm transition-opacity"
            style={{ left: `${(r.col / COLS) * 100}%`, top: r.y, width: `${100 / COLS}%`, height: TILE_H - 4,
              background: r.tapped ? "#0a3" : "#00F0FF", opacity: r.tapped ? 0.3 : 1, boxShadow: r.tapped ? "none" : "0 0 10px #00F0FF" }} />
        ))}
        {/* tap zones */}
        {running && (
          <div className="absolute inset-0 flex">
            {Array.from({ length: COLS }).map((_, i) => (
              <button key={i} onClick={() => tap(i)} data-testid={`piano-col-${i}`} className="flex-1 h-full" />
            ))}
          </div>
        )}
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-pink uppercase" data-testid="piano-over">Missed! Score {score}</p>}
            <Button onClick={start} data-testid="piano-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Tap the falling tile's column before it hits the bottom. Don't miss!</p>
    </div>
  );
}
